import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { database } from "../services/firebase";
import { ref, set, get } from "firebase/database";

import DisplayAd from "../components/DisplayAd";

export default function Subscription() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const checkExistingSubscription = async () => {
      try {
        if (!user) {
          setCheckingSubscription(false);
          return;
        }

        const subRef = ref(database, `subscriptions/${user.uid}`);
        const snapshot = await get(subRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const expiry = new Date(data.expiryTimestamp);

          if (data.premium && expiry > new Date()) {
            navigate("/premium-current-affairs");
            return;
          }
        }
      } catch (error) {
        console.error("Subscription auto-check failed:", error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkExistingSubscription();
  }, [user, navigate]);

  const cleanupRazorpayUI = () => {
    document.body.classList.remove("ReactModal__Body--open");
    document.body.style.overflow = "auto";

    const razorpayModal = document.querySelector(".razorpay-container");
    if (razorpayModal) razorpayModal.remove();

    const razorpayBackdrop = document.querySelector(".razorpay-backdrop");
    if (razorpayBackdrop) razorpayBackdrop.remove();
  };

  const startPayment = async () => {
    if (!user) {
      alert("⚠️ Please login first!");
      navigate("/login");
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      // ✅ Check existing subscription first
      const subRef = ref(database, `subscriptions/${user.uid}`);
      const snapshot = await get(subRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const expiry = new Date(data.expiryTimestamp);

        if (data.premium && expiry > new Date()) {
          alert("✅ You already have active premium!");
          navigate("/premium-current-affairs");
          setLoading(false);
          return;
        }
      }

      // ✅ Check Razorpay script
      if (!window.Razorpay) {
        alert("⚠️ Razorpay not loaded");
        setLoading(false);
        return;
      }

      const options = {
        key: "rzp_live_Sacn7ZCa8FEpkd",
        amount: 19900,
        currency: "INR",
        name: "UPSC Titans Premium",
        description: "Weekly Premium Subscription",
        prefill: {
          email: user.email || ""
        },
        theme: {
          color: "#16a34a"
        },

        handler: async function (response) {
          try {
            const paymentTimestamp = new Date().toISOString();
            const expiryTimestamp = new Date(
              Date.now() + 7 * 24 * 60 * 60 * 1000
            ).toISOString();

            await set(ref(database, `subscriptions/${user.uid}`), {
              premium: true,
              paymentId: response.razorpay_payment_id || "",
              paymentTimestamp,
              expiryTimestamp,
              email: user.email || "",
              uid: user.uid
            });

            cleanupRazorpayUI();
            alert("✅ Payment Successful!");
            navigate("/premium-current-affairs");
          } catch (err) {
            console.error("Firebase save error:", err);
            alert("❌ Payment success, but save failed.");
          } finally {
            setLoading(false);
          }
        },

        modal: {
          ondismiss: function () {
            cleanupRazorpayUI();
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        console.error("Payment failed:", response?.error);
        cleanupRazorpayUI();
        alert("❌ Payment failed. Please try again.");
        setLoading(false);
      });

      rzp.open();
    } catch (err) {
      console.error("Subscription check error:", err);
      alert("❌ Error checking subscription");
      setLoading(false);
    }
  };

  if (checkingSubscription) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top left, rgba(56,189,248,0.08), transparent 25%), radial-gradient(circle at top right, rgba(34,197,94,0.08), transparent 24%), linear-gradient(135deg, #020617, #0f172a, #111827)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff"
        }}
      >
        <div className="text-center">
          <div
            className="spinner-border"
            role="status"
            style={{ width: "3rem", height: "3rem", color: "#22c55e" }}
          ></div>
          <p className="mt-4" style={{ color: "#cbd5e1", fontWeight: 700 }}>
            Checking your premium access...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(56,189,248,0.08), transparent 25%), radial-gradient(circle at top right, rgba(34,197,94,0.08), transparent 24%), linear-gradient(135deg, #020617, #0f172a, #111827)",
        padding: "40px 16px"
      }}
    >
      <div className="container">
        <div className="row justify-content-center align-items-center g-5">
          <div className="col-lg-5 d-none d-lg-block">
            <div style={{ color: "#fff", paddingRight: "20px" }}>
              <div
                style={{
                  display: "inline-block",
                  padding: "8px 16px",
                  borderRadius: "999px",
                  background: "rgba(34,197,94,0.12)",
                  border: "1px solid rgba(34,197,94,0.18)",
                  color: "#bbf7d0",
                  fontWeight: 800,
                  fontSize: "13px",
                  letterSpacing: "1px",
                  marginBottom: "22px"
                }}
              >
                UPSC PREMIUM ACCESS
              </div>

              <h1
                style={{
                  fontSize: "3rem",
                  fontWeight: 900,
                  lineHeight: "1.15",
                  marginBottom: "18px"
                }}
              >
                Unlock Your
                <span
                  style={{
                    display: "block",
                    background: "linear-gradient(90deg, #38bdf8, #22c55e, #ffffff)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}
                >
                  Premium UPSC System
                </span>
              </h1>

              <p
                style={{
                  color: "#cbd5e1",
                  lineHeight: "1.9",
                  fontSize: "1.05rem"
                }}
              >
                Get access to premium current affairs, topic-wise question banks,
                guided notes, answer support and structured UPSC preparation.
              </p>

              <div className="mt-4 d-flex flex-column gap-3">
                <div style={benefitStyle}>✅ Weekly premium content access</div>
                <div style={benefitStyle}>✅ Smart preparation for serious aspirants</div>
                <div style={benefitStyle}>✅ Affordable and focused UPSC support</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6 col-xl-5">
            <div
              style={{
                borderRadius: "34px",
                padding: "34px 26px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(18px)",
                boxShadow: "0 25px 80px rgba(0,0,0,0.35)"
              }}
            >
              <div className="text-center mb-4">
                <div
                  style={{
                    width: "84px",
                    height: "84px",
                    borderRadius: "26px",
                    margin: "0 auto 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(34,197,94,0.12)",
                    border: "1px solid rgba(34,197,94,0.18)",
                    fontSize: "2rem"
                  }}
                >
                  👑
                </div>

                <h2 style={{ color: "#fff", fontWeight: 900, marginBottom: "8px" }}>
                  Weekly Premium
                </h2>
                <p style={{ color: "#94a3b8", marginBottom: "10px" }}>
                  Serious preparation. Simple pricing.
                </p>
              </div>

              <div
                style={{
                  borderRadius: "28px",
                  padding: "28px",
                  background:
                    "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(17,24,39,0.92))",
                  border: "1px solid rgba(255,255,255,0.08)",
                  textAlign: "center",
                  marginBottom: "24px"
                }}
              >
                <div style={{ color: "#94a3b8", fontWeight: 700, marginBottom: "8px" }}>
                  Premium Plan
                </div>

                <h1
                  style={{
                    fontSize: "3.2rem",
                    fontWeight: 900,
                    color: "#fff",
                    marginBottom: "10px"
                  }}
                >
                  ₹199
                </h1>

                <p style={{ color: "#cbd5e1", marginBottom: 0 }}>
                  7 Days Premium Access
                </p>
              </div>

              <div className="d-flex flex-column gap-3 mb-4">
                <div style={featureRowStyle}>📘 Premium Current Affairs</div>
                <div style={featureRowStyle}>📝 UPSC Notes & Topics</div>
                <div style={featureRowStyle}>📚 Practice Questions & Answers</div>
                <div style={featureRowStyle}>🎯 Smart Focused Learning System</div>
              </div>

              <button
                className="btn w-100"
                onClick={startPayment}
                disabled={loading}
                style={{
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "18px",
                  padding: "15px 22px",
                  fontWeight: 800,
                  fontSize: "1rem",
                  boxShadow: "0 14px 34px rgba(34,197,94,0.30)"
                }}
              >
                {loading ? "Processing..." : "Start Premium"}
              </button>

              <p
                className="text-center mt-3 mb-0"
                style={{ color: "#94a3b8", fontSize: "0.92rem", lineHeight: "1.8" }}
              >
                Secure payment with Razorpay. Premium access is activated instantly after successful payment.
              </p>
            </div>
          </div>
        </div>
      </div>

      <DisplayAd />
    </div>
  );
}

const benefitStyle = {
  padding: "14px 16px",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#e2e8f0",
  fontWeight: 600
};

const featureRowStyle = {
  padding: "14px 16px",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#f8fafc",
  fontWeight: 700
};